import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';
import type { CreativeSummary, MediaRef } from '@sl/core';
import { SurfaceModeChip } from '../app/shell/ModeChip';
import { overlayModule, useOverlayModule } from '../app/lazyOverlay';
import { Button, Icon, Modal, Skeleton, buttonClassName } from '../ui';

export interface PreviewAd {
  id: string;
  name: string;
  thumbnail: MediaRef | null;
  mediaType: CreativeSummary['mediaType'];
  href: string;
  /** The full ad page already contains analysis; use a larger media-only viewer there. */
  mediaOnly?: boolean;
}

type OpenPreview = (ad: PreviewAd, trigger: HTMLElement) => void;
const Context = createContext<OpenPreview | null>(null);
const contentModule = overlayModule(() => import('./AdPreviewModal'));
export const useAdPreview = () => useContext(Context);

/** One overlay for the whole account, rather than one modal/query set for every virtualized row. */
export function AdPreviewProvider({ children }: { children: ReactNode }) {
  const [ad, setAd] = useState<PreviewAd | null>(null);
  const Content = useOverlayModule(contentModule, ad !== null)?.default;
  const trigger = useRef<HTMLElement | null>(null);
  const location = useLocation();
  useEffect(() => setAd(null), [location.pathname, location.search]);
  const open = useCallback<OpenPreview>((selected, element) => {
    trigger.current = element;
    setAd(selected);
  }, []);
  const close = () => setAd(null);
  return (
    <Context.Provider value={open}>
      {children}
      {ad ? (
        <Modal
          open
          onClose={close}
          title={ad.name}
          subtitle="Ad preview"
          size="lg"
          returnFocusRef={trigger}
          modeChip={<SurfaceModeChip testID="ad-preview.mode-chip" />}
          testID="ad-preview"
          footer={
            <>
              <Button variant="ghost" onClick={close}>
                Close
              </Button>
              {ad.mediaOnly ? null : (
                <Link
                  to={ad.href}
                  onClick={close}
                  className={buttonClassName({ variant: 'primary' })}
                  data-testid="ad-preview.analysis"
                >
                  See more analysis <Icon name="arrow-right" size={16} />
                </Link>
              )}
            </>
          }
        >
          {Content ? <Content key={ad.id} ad={ad} /> : <Skeleton shape="block" height={360} />}
        </Modal>
      ) : null}
    </Context.Provider>
  );
}
